import { z } from 'zod';

/**
 * RBF core data model — the durable source of truth shared by the client and
 * the Supabase backend (Edge Functions). Keep these in lockstep with the
 * `supabase/migrations/` SQL and the PowerSync schema; drift here is where
 * offline/sync bugs hide.
 *
 * Lifecycle: template (durable) -> instance (live run) -> completed instance
 * (frozen, read-only history).
 */

export const uuid = z.string().uuid();
export const isoTimestamp = z.string().datetime({ offset: true });

/** A live run is `active`; once completed it freezes and becomes read-only. */
export const InstanceStatus = z.enum(['active', 'completed']);
export type InstanceStatus = z.infer<typeof InstanceStatus>;

/**
 * Assignable access level on a template's invite links and collaborators:
 *   member — participate in every run (start, check off, seal); read the template.
 *   editor — member + edit the template's content.
 *   admin  — editor + manage invite links and the roster.
 * The template's owner outranks all three (see AccessRole for roster display).
 */
export const TemplateRole = z.enum(['member', 'editor', 'admin']);
export type TemplateRole = z.infer<typeof TemplateRole>;

/** Roster display level: an assignable role plus the non-assignable `owner`. */
export const AccessRole = z.enum(['owner', 'admin', 'editor', 'member']);
export type AccessRole = z.infer<typeof AccessRole>;

const timestamps = {
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
};

export const Profile = z.object({
  id: uuid,
  display_name: z.string().min(1).max(120).nullable(),
  ...timestamps,
});
export type Profile = z.infer<typeof Profile>;

export const Template = z.object({
  id: uuid,
  owner_id: uuid,
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable(),
  /** Optional emoji icon shown in lists. */
  icon: z.string().max(16).nullable(),
  ...timestamps,
});
export type Template = z.infer<typeof Template>;

export const TemplateItem = z.object({
  id: uuid,
  template_id: uuid,
  position: z.number().int().nonnegative(),
  label: z.string().min(1).max(500),
  notes: z.string().max(2000).nullable(),
  ...timestamps,
});
export type TemplateItem = z.infer<typeof TemplateItem>;

export const Instance = z.object({
  id: uuid,
  template_id: uuid,
  owner_id: uuid,
  title: z.string().min(1).max(200),
  /** Snapshotted from the template at spin-up; frozen against later edits. */
  description: z.string().max(2000).nullable(),
  status: InstanceStatus,
  started_at: isoTimestamp,
  // When the run was sealed (client, offline-stamped, bounded to the run window).
  completed_at: isoTimestamp.nullable(),
  // When the server learned of the seal. Server-owned and non-forgeable; the instant
  // the record was frozen, which a later *_recorded_at is measured against.
  completed_recorded_at: isoTimestamp.nullable(),
  /** Who sealed the run; stamped server-side from the caller at seal time. */
  completed_by: uuid.nullable(),
  ...timestamps,
});
export type Instance = z.infer<typeof Instance>;

/**
 * Instance items snapshot the template at spin-up. `label`/`position` are copied
 * so later template edits never mutate a live or historical run.
 */
export const InstanceItem = z.object({
  id: uuid,
  instance_id: uuid,
  source_template_item_id: uuid.nullable(),
  position: z.number().int().nonnegative(),
  label: z.string().min(1).max(500),
  notes: z.string().max(2000).nullable(),
  checked: z.boolean(),
  // Who checked the item (attribution). The display name is resolved live from
  // TemplateMemberProfile (joined on template_id + this id), not snapshotted.
  checked_by: uuid.nullable(),
  // When the check was made (client, offline-stamped, bounded to the run window).
  checked_at: isoTimestamp.nullable(),
  // When the server learned of it. Server-owned and non-forgeable; later than the
  // run's completed_at means the check landed after the seal.
  checked_recorded_at: isoTimestamp.nullable(),
  // Denormalized owning template id — a server-stamped sync routing key so a
  // template collaborator syncs every run's items (see the sync rules).
  template_id: uuid.nullable(),
  ...timestamps,
});
export type InstanceItem = z.infer<typeof InstanceItem>;

/**
 * A single append-only, immutable log note on a run step. 0–n per item; a new
 * entry per note (no editing), so concurrent loggers never collide. Distinct
 * from a template item's single last-write-wins reference `notes`.
 */
export const InstanceItemNote = z.object({
  id: uuid,
  instance_id: uuid,
  instance_item_id: uuid,
  // Who wrote the note. The display name is resolved live from
  // TemplateMemberProfile (joined on template_id + this id), not snapshotted.
  author_id: uuid.nullable(),
  body: z.string().min(1).max(4000),
  // When the note was written (client, offline-stamped, bounded to the run window);
  // the log reads in this order.
  created_at: isoTimestamp,
  // When the server learned of it. Server-owned and non-forgeable; later than the
  // run's completed_at means the note landed after the seal.
  recorded_at: isoTimestamp.nullable(),
  // Denormalized owning template id — server-stamped sync routing key, matching
  // InstanceItem.template_id.
  template_id: uuid.nullable(),
});
export type InstanceItemNote = z.infer<typeof InstanceItemNote>;

/** A role-tagged invite link. Redeeming it makes the caller a collaborator. */
export const TemplateShare = z.object({
  id: uuid,
  template_id: uuid,
  token: z.string().min(1),
  role: TemplateRole,
  created_by: uuid,
  ...timestamps,
});
export type TemplateShare = z.infer<typeof TemplateShare>;

/** Someone granted access to a template via an invite link, with their role. */
export const TemplateCollaborator = z.object({
  template_id: uuid,
  user_id: uuid,
  role: TemplateRole,
  joined_at: isoTimestamp,
});
export type TemplateCollaborator = z.infer<typeof TemplateCollaborator>;

/**
 * A member's display name projected per template — the live source of run
 * attribution. Maintained server-side (a member joins / a template is created /
 * a profile is renamed) and synced to everyone with template access, so the
 * client resolves `checked_by` / `author_id` to a name by joining on
 * (template_id, user_id) offline. Removed only when the account is deleted, so a
 * departed collaborator keeps their attribution while a deleted one shows as
 * "Unknown user".
 */
export const TemplateMemberProfile = z.object({
  id: uuid,
  template_id: uuid,
  user_id: uuid,
  display_name: z.string().max(120).nullable(),
});
export type TemplateMemberProfile = z.infer<typeof TemplateMemberProfile>;
